import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaHistory, FaBarcode, FaBox, FaCheck, FaCog, FaEdit, FaEllipsisV, FaFileDownload, FaFileUpload, FaImage, FaPlus, FaSearch, FaSync, FaTag, FaTrash } from 'react-icons/fa';
import { Badge, Button, Card, CardBody, Col, Container, DropdownItem, DropdownMenu, DropdownToggle, Input, Modal, ModalBody, ModalHeader, Row, Table, UncontrolledDropdown } from 'reactstrap';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { productActions } from '../../sagas/productSlice';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import config from '../../helpers/config';
import { PRODUCTS_FORM_PAGE } from '../../routes/routeConstants';
import { useLocation, useNavigate } from 'react-router-dom';
import { categoryActions } from '../../sagas/categorySlice';
import { formatPrice } from '../../helpers/utility';
import './ProductList.css';
import Select from "react-select";

const ProductList = () => {
  const location = useLocation();
  const savedList = location.state?.productList;
  const searchTimer = useRef(null);
  const [previewProduct, setPreviewProduct] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef(null);
  const [displayMode, setDisplayMode] = useState(savedList?.displayMode || 'table');
  const [currentPage, setCurrentPage] = useState(savedList?.currentPage || 1);
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const unites = useSelector(status => status.products.unites); // Assuming you have a Redux store with unites
  const { list } = useSelector(state => state.categories); // Assuming you have a Redux store with categories

  const [filters, setFilters] = useState({
    categorieId: savedList?.filters?.categorieId ?? null,
    query: savedList?.filters?.query || ''
  });

  const {
    products,
    loading,
    error,
    totalElements,
    totalPages,
    pageNumber,
    pageSize
  } = useSelector((state) => state.products);
  const navigate = useNavigate();

  useEffect(() => {
    navigate(location.pathname + location.search, {
      replace: true,
      state: { ...location.state, productList: { filters, currentPage, displayMode } },
    });
  }, [filters, currentPage, displayMode]);

  const openHistory = product => {
    navigate('/inventory-module/movements?articleId=' + encodeURIComponent(product.id), {
      state: {
        articleName: product.libelle,
        articlesReturnTo: location.pathname + location.search,
        productList: { filters, currentPage, displayMode },
      },
    });
  };


  useEffect(() => {
    // Auto focus on mount
    searchInputRef.current?.focus();

    // Add keyboard shortcut
    const handleKeyDown = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    dispatch(categoryActions.list({ query: '' })); // Fetch categories on component mount
    dispatch(productActions.findAllUnites());

  }, []);


  useEffect(() => {
    searchTimer.current = setTimeout(() => {
      dispatch(productActions.findAllArticles({ ...filters, page: currentPage - 1, size: 10 }));
    }, 150);
    return () => clearTimeout(searchTimer.current);
  }, [dispatch, currentPage, filters.categorieId, filters.query]);


  // Determine the correct client form path based on current context
  const getProductFormPath = () => {
    // If we're in a sales module route, use relative path
    if (location.pathname.startsWith('/inventory-module')) {
      return '/inventory-module/products/new';
    }
    // Otherwise use the legacy absolute path
    return PRODUCTS_FORM_PAGE;
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      debouncedSearch(); // Trigger search when Enter is pressed
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, query: value }));
  };

  // Debounced search function
  const debouncedSearch = () => {
    clearTimeout(searchTimer.current);
    if (currentPage !== 1) { setCurrentPage(1); return; }
    dispatch(productActions.findAllArticles({
      ...filters,
      page: 0,
      size: 10,
    }));
  }



  const refrechProducts = () => {
    setIsSearching(true);
    dispatch(productActions.findAllArticles({
      ...filters,
      page: 0,
      size: 10,
    }));
  }


  const handleDelete = (productId) => {
    Swal.fire({
      title: t('product.deleteConfirmTitle'),
      text: t('product.deleteConfirmText'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: t('common.confirm'),
      cancelButtonText: t('common.cancel')
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {

          let payload = {
            id: productId,
            onSuccess: () => {
              Swal.fire(t('product.deleted'), '', 'success');
              refrechProducts();
            },
            onError: (error) => {
              Swal.fire({
                icon: 'error',
                title: t('product.deleteError'),
                text: t('product.deleteErrorMessage')
              });
            }
          }
          dispatch(productActions.deleteProduct(payload));

        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: t('common.error'),
            text: t('product.deleteError')
          });
        }
      }
    });
  };

  const handleActiverArticle = (productId) => {
    Swal.fire({
      title: 'ÃŠtes-vous sÃ»r ?',
      text: "Le produit sera activÃ© et pourra Ãªtre utilisÃ©.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, activer !',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        let payload = {
          id: productId,
          onSuccess: () => {
            Swal.fire(
              'ActivÃ© !',
              'Le produit a Ã©tÃ© activÃ© avec succÃ¨s.',
              'success'
            );
            refrechProducts();
          },
          onError: () => {
            Swal.fire(
              'Erreur !',
              'Une erreur s\'est produite lors de l\'activation.',
              'error'
            );
          }
        }
        dispatch(productActions.activate(payload));
      }
    });
  };



  const handleEdit = (product) => {
    navigate("/inventory-module/products/edit", { state: { product } });
  };



  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const rows = [
      ['Libelle', 'Code à barre', 'Famille / Catégorie', 'Prix U.', 'Prix Achat', 'Unité', 'Qty'],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const colWidths = [
      { wch: 35 },
      { wch: 20 },
      { wch: 30 },
      { wch: 14 },
      { wch: 14 },
      { wch: 12 },
      { wch: 12 },
    ];
    ws['!cols'] = colWidths;
    XLSX.utils.book_append_sheet(wb, ws, 'Item Price');
    XLSX.writeFile(wb, 'produit_template.xlsx');
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      const hasExcelExtension = /\.(xlsx|xls)$/i.test(file.name || '');
      if (!allowedTypes.includes(file.type) && !hasExcelExtension) {
        Swal.fire({
          icon: 'error',
          title: t('product.invalidFileType'),
          text: t('product.excelOnly'),
        });
        return;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);

        let payload = {
          formData: formData,
          onSuccess: (result = {}) => {
            const details = [
              `${Number(result.created || 0)} article(s) créé(s).`,
              `${Number(result.skipped || 0)} ligne(s) ignorée(s) car déjà existantes ou dupliquées.`,
              `${Number(result.failed || 0)} ligne(s) en erreur.`,
            ];
            if (Array.isArray(result.errors) && result.errors.length) {
              details.push('', 'Premières erreurs :', ...result.errors.slice(0, 10));
            }
            Swal.fire({
              icon: Number(result.failed || 0) > 0 ? 'warning' : 'success',
              title: 'Import des articles terminé',
              text: details.join('\n'),
              confirmButtonText: 'OK',
              confirmButtonColor: '#198754'
            });
            refrechProducts();
          },
          onError: (error) => {
            Swal.fire({
              icon: 'error',
              title: "Échec de l'import",
              text: error?.response?.data?.message || error?.response?.data?.error || "Erreur lors de l'importation des produits",
              confirmButtonText: 'OK',
              confirmButtonColor: '#dc3545'
            });
          },
        }
        dispatch(productActions.uploadProducts(payload));

      } catch (error) {
        console.error('Error parsing Excel:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erreur!',
          text: "Erreur lors de l'importation des produits",
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545'
        });
      } finally {
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  }

  const DisplayToggle = () => (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <div className="btn-group">
        {/* <Button
          color={displayMode === 'table' ? 'primary' : 'light'}
          onClick={() => setDisplayMode('table')}
          size="sm"
        >
          <FaList className="me-1" /> {t('common.table')}
        </Button>
        <Button
          color={displayMode === 'card' ? 'primary' : 'light'}
          onClick={() => setDisplayMode('card')}
          size="sm"
        >
          <FaTh className="me-1" /> {t('common.cards')}
        </Button> */}
      </div>

      <div className="d-flex align-items-center gap-3">
        <small className="text-muted">
          {t('product.showing', {
            start: (currentPage - 1) * pageSize + 1,
            end: Math.min(currentPage * pageSize, totalElements),
            total: totalElements,
            currentPage: currentPage,
            totalPages: totalPages
          })}
        </small>

        <div className="d-flex align-items-center gap-2">
          <Button color="light" size="sm" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
            <FaAngleDoubleLeft />
          </Button>
          <Button color="light" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
            <FaAngleLeft />
          </Button>

          <span className="mx-2">
            {currentPage} / {totalPages}
          </span>

          <Button color="light" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
            <FaAngleRight />
          </Button>
          <Button color="light" size="sm" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>
            <FaAngleDoubleRight />
          </Button>
        </div>
      </div>
    </div>
  );

  const TableView = () => (
    <>
      <div className="product-table-wrap">
        <Table hover bordered className="align-middle mb-0">
          <thead className="bg-light">
            <tr>
              <th className="align-middle text-center product-photo-column">Photo</th>
              <th className="align-middle">
                <FaBox className="me-2 text-primary" size={12} />
                {t('product.description')} / {t('product.category')}
              </th>
              <th className="align-middle text-end">
                <FaTag className="me-2 text-primary" size={12} />
                {t('product.salePrice')} / {t('product.buyingPrice')}
              </th>
              <th className="align-middle text-center">
                <FaTag className="me-2 text-primary" size={12} />
                {t('product.stockDisponible')} / {t('client.active')}
              </th>
              <th className="align-middle text-center" style={{ width: '120px' }}>
                <FaCog className="me-2 text-primary" size={12} />
                {t('common.actions')}
              </th>
            </tr>
          </thead>

	          <tbody>
	            {!products || products.length === 0 ? (
	              <tr>
			                <td colSpan="5" className="text-center py-4">
	                  <div className="text-muted">
	                    {t('product.noProducts')}
	                  </div>
	                </td>
	              </tr>
	            ) : (
	              products.map((product) => (
	                <tr key={product.id}>
                  <td className="align-middle text-center">
                    {product.photo ? (
                      <button
                        type="button"
                        className="product-thumbnail-button"
                        onClick={() => setPreviewProduct(product)}
                        title={product.libelle}
                      >
                        <img
                          src={`${config.baseURL}${product.photo}`}
                          alt={product.libelle}
                          className="product-thumbnail"
                        />
                      </button>
                    ) : <FaImage className="text-muted" size={24} />}
                  </td>
                  <td className="align-middle">
                    <div className="fw-semibold">{product.libelle}</div>
                    <div className="text-muted small">
                      {[product.code, product.codeBarre, product.categorieLibelle,
                        unites.find(u => u.code === product.unite)?.libelle]
                        .filter(Boolean).join(' · ')}
                    </div>
                  </td>
                  <td className="align-middle text-end">
                    <div>{formatPrice(product.prixVenteTTC1)}</div>
                    <div className="text-muted small">Achat: {formatPrice(product.prixAchatTTC)}</div>
                  </td>
                  <td className="align-middle text-center">
                    <div className="mb-1">{product.stockDisponible}</div>
                    {product.actif ? (
                      <Badge color="success">Actif</Badge>
                    ) : (
                      <Badge color="danger">BloquÃ©</Badge>
                    )}
                  </td>
                  <td className="align-middle text-center">
                    <Button color="info" size="sm" className="mb-1" title="Historique du stock" aria-label={`Historique du stock de ${product.libelle}`} onClick={() => openHistory(product)}><FaHistory /></Button>
                    <Button color="warning" size="sm" className="me-1 px-2 py-1">
                      <FaEdit size={12} onClick={() => handleEdit(product)} />
                    </Button>


                    {product.actif ? <Button color="danger" size="sm" className="px-2 py-1">
                      <FaTrash size={12} onClick={() => handleDelete(product.id)} />
                    </Button>
                      :
                      <Button
                        className="ms-1"
                        color="success"
                        size="sm"
                        title='Activer'
                        onClick={() => handleActiverArticle(product.id)}
                      >
                        <FaCheck />
                      </Button>
                    }
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          {t('product.showing', {
            start: (currentPage - 1) * pageSize + 1,
            end: Math.min(currentPage * pageSize, totalElements),
            total: totalElements,
            currentPage: currentPage,
            totalPages: totalPages
          })}
        </small>
      </div>
    </>
  );





  const CardView = () => {
    return (
      <div className="product-card-wrap">
        <Row xs={2} md={4} lg={5} className="g-3">
          {(!products || products.length === 0) && (
            <Col xs={12} className="text-center py-4 ">
              {t('product.noProducts')}
            </Col>
          )}
          {
		            products.map(product => (
		              <Col key={product.id}>
		                <Card className="product-card rounded border shadow-sm">
	                  <div className="card-image-container">
	                    {product.photo ? (
                      <img
                        src={`${config.baseURL}${product.photo}`}
                        alt={product.libelle}
                        className="card-img-top rounded-top"
                      />
                    ) : (
                      <div className="bg-placeholder rounded-top d-flex align-items-center justify-content-center">
                        <FaImage size={32} className="text-muted" />
                      </div>
                    )}
                  </div>
                  <CardBody className="d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start">
                      <h6 className="product-title mb-2">
                        {product.libelle}
                      </h6>
                      <div className="action-buttons d-flex">
                        <Button color="info" size="sm" title="Historique du stock" aria-label={`Historique du stock de ${product.libelle}`} onClick={() => openHistory(product)}><FaHistory /></Button>
                        <Button color="warning" size="sm" className="me-1" onClick={() => handleEdit(product)}>
                          <FaEdit size={12} />
                        </Button>
                        <Button color="danger" size="sm" onClick={() => handleDelete(product.id)}>
                          <FaTrash size={12} />
                        </Button>
                      </div>
                    </div>

                    <p className="product-price mb-1">
                      {product.unite ? `${formatPrice(product.prixVenteTTC1)} / ${product.unite}` : 'N/A'}
                    </p>

                  </CardBody>
                </Card>
              </Col>
            ))
          }
        </Row>
      </div>
    );
  };



  return (
    <>
    <Container fluid className="product-list-page">
      <Row>
        <Col md={12}>
          <Card className="shadow-sm h-100">
            <CardBody>

                <Row className="mb-3 g-2 align-items-end product-toolbar">
                  <Col md={5}>
                    {/* Search Input - Left Side */}
                    <Input
                      innerRef={searchInputRef}
                      type="text"
                      placeholder={t('product.searchPlaceholder')}
                      value={filters.query}
                      onKeyDown={handleSearchKeyDown}
                      onChange={handleSearchChange}
                      className={`ps-4 ${isSearching ? 'searching' : ''}`}
                    />
                  </Col>
                  <Col md={4}>
                    <Select
                      name="categorie"
                      options={list.map(cat => ({ value: cat.id, label: cat.libelle }))}
                      value={list.map(cat => ({ value: cat.id, label: cat.libelle }))
                        .find(option => option.value === filters.categorieId) || null}
                      onChange={(selected) => { setCurrentPage(1); setFilters(prev => ({ ...prev, categorieId: selected ? selected.value : null })); }}
                      placeholder="Rechercher une catégorie..."
                      isClearable
                      isSearchable
                    />

                  </Col>
                  <Col md={3}>
                    <div className="product-toolbar-actions">
                      <Button color="primary" size="sm"
                        className="me-2" onClick={() => navigate(getProductFormPath())}>
                        <FaPlus className="me-2" />
                        {t('product.new')}
                      </Button>


                      <UncontrolledDropdown>
                        <DropdownToggle color="light" size="sm" className="border">
                          <FaEllipsisV />
                        </DropdownToggle>
                        <DropdownMenu end>
                          <DropdownItem
                            onClick={() =>
                              dispatch(productActions.updateGlobalStock({
                                onSuccess: () => {
                                  refrechProducts()
                                },
                                onError: () => {
                                }
                              }))
                            }
                          >
                            <FaSync className="me-2" />
                            {t('product.refreshStock')}
                          </DropdownItem>
                          <DropdownItem onClick={() => fileInputRef.current.click()}>
                            <FaFileUpload className="me-2" size={12} />
                            {t('product.uploadProducts')}
                          </DropdownItem>
                          <DropdownItem onClick={downloadTemplate}>
                            <FaFileDownload className="me-2" size={12} />
                            {t('product.downloadTemplate')}
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>

                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                      />
                      
                      {/* <Button
                      color="secondary"
                      size="sm"
                      className="me-2"
                      onClick={downloadTemplate}
                    >
                      <FaFileDownload className="me-2" size={12} />
                      {t('product.downloadTemplate')}
                    </Button>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => fileInputRef.current.click()}
                      >
                        <FaFileUpload className="me-2" size={12} />
                        {t('product.uploadProducts')}
                      </Button>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                      />*/}
                    </div>
                  </Col>
                </Row>
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('common.loading')}</span>
                  </div>
                  <div className="mt-2 text-muted">
                    {t('common.loadingData')}
                  </div>
                </div>
              ) : (<>
                <DisplayToggle />
                {displayMode === 'table' ? <TableView /> : <CardView />}
              </>)}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
      <Modal isOpen={Boolean(previewProduct)} toggle={() => setPreviewProduct(null)} centered size="lg">
        <ModalHeader toggle={() => setPreviewProduct(null)}>
          {previewProduct?.libelle}
        </ModalHeader>
        <ModalBody className="text-center">
          {previewProduct?.photo && (
            <img
              src={`${config.baseURL}${previewProduct.photo}`}
              alt={previewProduct.libelle}
              className="product-photo-preview"
            />
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default ProductList;
